import AddIcon from '@mui/icons-material/Add'
import { Alert, Box, Button, FormControl, FormLabel } from '@mui/material'
import { difference, get } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import RemoveLoadingHook from '../../hooks/RemoveLoadingHook'
import { useActions } from '../../hooks/useActions'
import { useFamilyHelperHook } from '../../hooks/useFamilyHelperHook'
import { pathToPerson } from '../../utils/helpers'
import AddNewModal from '../../widgets/AddNewModal'
import PersonsList from '../../widgets/PersonList'
import SiblingQuestions from './SiblingQuestions'

function Siblings({ targetPerson, resolvePersonId }) {
  const id = typeof resolvePersonId === 'function' ? resolvePersonId(true) : resolvePersonId
  const familyPath = {
    proband: 'your-family',
    mother: 'mothers-family',
    father: 'fathers-family',
  }[targetPerson]
  const { setSiblings } = useActions()
  const [siblingPersonIds, setSiblingPersonIds] = useState([])
  const [siblingId, setSiblingId] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const addedNewSibling = useRef(false)
  const config = useSelector((state) =>
    get(state, [
      'configuration',
      'sectionConfig',
      familyPath,
      'subSectionConfig',
      'siblings',
      'questions',
    ]),
  )
  const { persons, getSiblingIds } = useFamilyHelperHook({ id })
  const probandId = useSelector((state) => get(state, ['questionnaire', 'probandId']))
  const adopted = useSelector((state) => get(state, [...pathToPerson(id), 'adopted']))
  const isProband = targetPerson === 'proband'

  RemoveLoadingHook()

  useEffect(() => {
    const newIds = getSiblingIds()
    if (addedNewSibling.current) {
      openModal(difference(newIds, siblingPersonIds)[0])
      addedNewSibling.current = false
    }
    setSiblingPersonIds(newIds)

    return () => {
      setSiblingPersonIds([])
    }
  }, [persons, id])

  return isProband || adopted !== 'Y' ? (
    <FormControl sx={{ width: '100%' }}>
      <FormLabel sx={{ pb: 1 }}>
        {isProband ? 'Please add your siblings' : "Please add this person's siblings"}
      </FormLabel>
      <PersonsList personIds={siblingPersonIds} openModal={openModal} />
      <AddNewModal
        open={isOpen}
        handleClose={closeModal}
        onSubmit={closeModal}
        dialogTitle="Add Sibling"
      >
        <SiblingQuestions
          personId={siblingId}
          isHalfSibling={isHalfSibling(siblingId)}
          resolvePersonId={id}
          isProband={isProband}
          probandId={probandId}
          config={config}
        />
      </AddNewModal>
      <Box pt={2} display="flex" gap={2} flexWrap="wrap">
        {[
          { isHalf: false, label: 'Add Sibling' },
          { isHalf: true, label: 'Add Half-Sibling' },
        ].map(({ isHalf, label }) => (
          <Button
            key={label}
            startIcon={<AddIcon />}
            onClick={() => buttonHandler(isHalf)}
            sx={{ width: 'fit-content' }}
            variant="contained"
          >
            {label}
          </Button>
        ))}
      </Box>
    </FormControl>
  ) : (
    <Alert severity="info">
      As this person is adopted, you do not need to provide information about their siblings.
    </Alert>
  )

  /**
   * Adds a new sibling when button is clicked
   * @param { Boolean } isHalf
   */
  function buttonHandler(isHalf) {
    const targetPersonArray = ['proband']
    if (['mother', 'father'].includes(targetPerson)) {
      targetPersonArray.push(targetPerson)
    }

    setSiblings({
      designatedPersonId: id,
      numberSharedParents: isHalf ? 'half' : 'full',
      targetPersonArray,
    })
    addedNewSibling.current = true
  }

  /**
   * Returns if a person is a half sibling
   * @param {String} personId
   * @returns {Boolean}
   */
  function isHalfSibling(personId) {
    const { relationshipToProband = '' } = persons[personId] || {}

    return relationshipToProband.includes('Half')
  }

  /**
   * Handle opening a new person modal
   */
  function openModal(clickId) {
    setIsOpen(true)
    setSiblingId(clickId)
  }

  /**
   * Close modal and reset siblingId
   */
  function closeModal() {
    setIsOpen(false)
    setSiblingId('')
  }
}

export { Siblings }
